import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Stethoscope, Receipt, Glasses, Tag, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [visitType, setVisitType] = useState("new");
  const [doctorId, setDoctorId] = useState("");
  const [chief, setChief] = useState("");
  const [abhaNumber, setAbhaNumber] = useState("");
  const [abhaAddress, setAbhaAddress] = useState("");

  const load = async () => {
    const { data } = await api.get(`/patients/${id}`);
    setData(data);
    setAbhaNumber(data.patient.abha_number || "");
    setAbhaAddress(data.patient.abha_address || "");
  };

  useEffect(() => {
    load();
    api.get("/hospital/doctors").then(({ data }) => setDoctors(data.items));
    // eslint-disable-next-line
  }, [id]);

  if (!data) return <div className="text-sm text-ink-muted">Loading…</div>;
  const p = data.patient;

  const createVisit = async () => {
    try {
      const { data: visit } = await api.post(`/patients/${p.id}/visits`, {
        patient_id: p.id, visit_type: visitType, doctor_id: doctorId || null, chief_complaint: chief, branch_id: p.branch_id,
      });
      toast.success(`Visit created · Token #${visit.token_no}`);
      navigate(`/app/opd/consult/${visit.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const linkAbha = async () => {
    try {
      await api.post("/abdm/abha/link", { patient_id: p.id, abha_number: abhaNumber, abha_address: abhaAddress, method: "manual" });
      toast.success("ABHA linked (stub)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="section-label">Patient profile</div>
            <h1 className="font-heading text-3xl font-semibold">{p.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
              <span className="font-mono text-xs">{p.uhid}</span>
              <span>·</span><span className="capitalize">{p.gender}</span>
              {p.dob && <><span>·</span><span>DOB {p.dob}</span></>}
              <span>·</span><span>{p.mobile}</span>
              <span>·</span><span className="capitalize">{p.category}</span>
              {p.abha_number && <Badge variant="outline" className="border-leaf/40 bg-leaf-50 text-leaf-700"><ShieldCheck size={11} className="mr-1" />ABHA Linked</Badge>}
            </div>
          </div>
          <div className="flex gap-2">
            <a href={`/print/opd-sticker/${p.id}`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="patient-print-sticker-btn"><Tag size={14} className="mr-2" />Print OPD sticker</Button></a>
            <a href={`/print/prescription-card/${p.id}`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="patient-print-opd-card-btn"><ClipboardList size={14} className="mr-2" />Print OPD card</Button></a>
            <Link to={`/app/appointments?patient_id=${p.id}`}><Button variant="outline" data-testid="patient-book-followup-btn"><Stethoscope size={14} className="mr-2" />Book follow-up</Button></Link>
            <Link to="/app/billing/new" state={{ patient: p }}><Button variant="outline" data-testid="patient-bill-btn"><Receipt size={14} className="mr-2" />Create Invoice</Button></Link>
          </div>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <div className="section-label mb-3">Start new OPD visit</div>
          <div className="form-grid">
            <div>
              <Label>Visit type</Label>
              <Select value={visitType} onValueChange={setVisitType}>
                <SelectTrigger data-testid="visit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Doctor</Label>
              <Select value={doctorId} onValueChange={setDoctorId}>
                <SelectTrigger data-testid="visit-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Chief complaint (optional)</Label>
              <Input value={chief} onChange={e => setChief(e.target.value)} data-testid="visit-chief-input" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={createVisit} className="bg-brand hover:bg-brand-700" data-testid="visit-create-btn">
              <Stethoscope size={14} className="mr-2" /> Start visit & token
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <div className="section-label mb-3">ABHA / ABDM</div>
          <Label>ABHA Number</Label>
          <Input value={abhaNumber} onChange={e => setAbhaNumber(e.target.value)} placeholder="14-digit" data-testid="abha-number-input" />
          <Label className="mt-3 block">ABHA Address</Label>
          <Input value={abhaAddress} onChange={e => setAbhaAddress(e.target.value)} placeholder="e.g. user@sbx" data-testid="abha-address-input" />
          <Button onClick={linkAbha} className="mt-4 w-full bg-leaf hover:bg-leaf-600" data-testid="abha-link-btn">
            <ShieldCheck size={14} className="mr-2" /> Link / Update ABHA
          </Button>
          <div className="mt-2 text-[11px] text-ink-muted">Sandbox stub · plug ABDM keys to go live.</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-border p-4 font-heading text-base font-semibold">Visit history</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-left">Token</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.visits.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-ink-muted">No visits yet.</td></tr>}
              {data.visits.map(v => (
                <tr key={v.id} className="row-enter hover:bg-secondary/40">
                  <td className="px-4 py-2">{v.visit_date}</td>
                  <td className="px-4 py-2 font-mono">#{v.token_no}</td>
                  <td className="px-4 py-2 capitalize">{v.visit_type.replace("_"," ")}</td>
                  <td className="px-4 py-2 capitalize">{v.status.replace("_"," ")}</td>
                  <td className="px-4 py-2 text-right">
                    <Link to={`/app/opd/consult/${v.id}`}><Button variant="outline" size="sm"><Stethoscope size={12} className="mr-1" />Open</Button></Link>
                    <Link to={`/app/optometry?visit=${v.id}`} className="ml-2"><Button variant="outline" size="sm"><Glasses size={12} className="mr-1" />Optometry</Button></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
